import type { NavigationItem } from '../../../tokens/navigationPaths';
import { BASE_NAV_PATHS } from '../../../tokens/navigationPaths';

/**
 * Looks up a navigation path by traversing the label hierarchy in BASE_NAV_PATHS.
 *
 * This function provides runtime validation - it will throw a clear error if the
 * path doesn't exist. For type-safe usage, use the `SettingsPaths` helpers which
 * use const assertions.
 *
 * @example
 * // Direct usage with runtime validation
 * getNavPath(['Settings', undefined, 'General'])
 * // Returns: '/settings/general'
 *
 * @example
 * // Type-safe usage via helpers (recommended)
 * SettingsPaths.General()
 * // Returns: '/settings/general'
 *
 * @param labels - Array of labels representing the hierarchy to traverse, use undefined for groups with no label
 * @returns The path of the matching navigation item
 * @throws Error if the label path doesn't exist in BASE_NAV_PATHS
 */
export function getNavPath(labels: readonly (string | undefined)[]): string {
  let currentItems: NavigationItem[] = BASE_NAV_PATHS;
  let currentPath: string | undefined;

  for (const label of labels) {
    const found = currentItems.find(item => item.label === label);

    if (!found) {
      throw new Error(
        `Navigation path not found for labels: ${JSON.stringify(labels)}. ` +
          `Could not find "${label}" in current navigation level.`
      );
    }

    currentPath = found.path;
    currentItems = found.children || [];
  }

  if (!currentPath) {
    throw new Error(
      `Navigation path not found for labels: ${JSON.stringify(labels)}. The matching item has no path defined.`
    );
  }

  return currentPath;
}

/**
 * Navigation path lookups for Settings routes.
 *
 * @example
 * navigate(SettingsPaths.General())
 * // Returns: '/settings/general'
 */
export const SettingsPaths = {
  // User Settings
  General: () => getNavPath(['User Settings', undefined, 'General'] as const),
  Trading: () => getNavPath(['User Settings', undefined, 'Trading'] as const),
  ApiKey: () => getNavPath(['User Settings', undefined, 'API Key'] as const),
  UserNotifications: () => getNavPath(['User Settings', undefined, 'User Notifications'] as const),
};

/**
 * Navigation path lookups for Admin routes.
 *
 * @example
 * navigate(AdminPaths.SubAccounts())
 * // Returns: '/admin/org-config/sub-accounts'
 */
export const AdminPaths = {
  // Trading
  TradingAggregations: () => getNavPath(['Admin', 'Trading', 'Trading Aggregations'] as const),
  TradingControls: () => getNavPath(['Admin', 'Trading', 'Trading Controls'] as const),
  TradingLimits: () => getNavPath(['Admin', 'Trading', 'Trading Limits'] as const),
  UnifiedLiquidity: () => getNavPath(['Admin', 'Trading', 'Unified Liquidity'] as const),

  // Org Config
  SubAccounts: () => getNavPath(['Admin', 'Sub Accounts'] as const),
  AllocationControls: () => getNavPath(['Admin', 'Allocation Controls'] as const),
  Users: () => getNavPath(['Admin', undefined, 'Users'] as const),
  UserGroups: () => getNavPath(['Admin', 'User Groups'] as const),

  // Notifications
  RuleManagement: () => getNavPath(['Admin', 'Notifications', 'Rule Management'] as const),
  Destinations: () => getNavPath(['Admin', 'Notifications', 'Destinations'] as const),

  // Connectivity
  Addresses: () => getNavPath(['Admin', 'Connectivity', 'Addresses'] as const),
  Counterparties: () => getNavPath(['Admin', 'Connectivity', 'Counterparties'] as const),
  Credentials: () => getNavPath(['Admin', 'Connectivity', 'Credentials'] as const),
  MarketAccounts: () => getNavPath(['Admin', 'Connectivity', 'Market Accounts'] as const),

  // Security Master
  SecurityMaster: () => getNavPath(['Admin', 'Security Master', 'Security Master'] as const),
  MultilegSecurityMaster: () => getNavPath(['Admin', 'Security Master', 'Multileg Security Master'] as const),
} as const;

export const TalosAdminPaths = {
  Default: () => getNavPath(['Talos Admin'] as const),
  Users: () => getNavPath(['Talos Admin', undefined, 'Users'] as const),
  MarketConnections: () => getNavPath(['Talos Admin', 'Operations', 'Market Connections'] as const),
};
